"use client";

import {
  FileCode,
  FileJson,
  FileSpreadsheet,
  FileText,
  FileType,
} from "lucide-react";

// Tool text mappings
const TOOL_TEXT_MAP = {
  file_tool: "Working on the file",
  search: "Searching the internet",
  search_web: "Searching the internet",
  generateChart: "Generating chart",
  charts: "Generating chart",
  generate_chart: "Generating chart",
  createDocument: "Creating document",
  updateDocument: "Updating document",
  generateImageTool: "Generating image",
  generate_image: "Generating image",
  requestSuggestions: "Requesting suggestions",
  weather: "Getting weather",
  querySql: "Querying SQL",
  data_catalog_search: "Searching data for: ",
  create_data_analytics_plan: "Creating data analytics plan",
  generate_visualization: "Generating visualization",
  execute_code: "Executing code",
  make_agent_call: "Calling ",
  createTask: "Scheduling task",
  searchInternet: "Searching internet:",
  sendEmail: "Sending email to",
  send_email_tool: "Sending email to",

  // Task Management Tools
  create_task: "Creating task",
  list_tasks: "Listing tasks",
  get_task_status: "Getting task status",
  execute_task_now: "Executing task now",
  pause_task: "Pausing task",
  resume_task: "Resuming task",
  delete_task: "Deleting task",
  trigger_event: "Triggering event",

  // File Management
  file_management_tool: "Managing files",

  // Browser Tools
  browser_navigate_to: "Navigating to URL",
  browser_go_back: "Going back",
  browser_wait: "Waiting",
  browser_click_element: "Clicking element",
  browser_input_text: "Typing text",
  browser_extract_elements: "Extracting elements",
  browser_scroll_down: "Scrolling down",
  browser_scroll_up: "Scrolling up",
  browser_scroll_to_text: "Scrolling to text",
  browser_take_screenshot: "Taking screenshot",
  browser_send_keys: "Sending keys",
  browser_switch_tab: "Switching tab",
  browser_close_tab: "Closing tab",
  browser_get_dropdown_options: "Getting dropdown options",
  browser_select_dropdown_option: "Selecting dropdown option",
  browser_drag_drop: "Drag and drop",
  browser_click_coordinates: "Clicking coordinates",
  browser_list_inputs: "Listing inputs",

  // SubAgent tools
  website_scraping: "Scraping website",
  content_parser_tool: "Parsing HTML content",
  content_extraction: "Extracting content insights",
  apollo_company_search: "Searching companies",
  google_keyword_planner: "Planning keywords",
  dataforseo_api: "Analyzing SEO data",
  content_gap_analysis: "Analyzing content gaps",
  claude_content_generator: "Generating content with AI",
  language_tool_grammar_checker: "Checking grammar",
  duplichecker_plagiarism: "Checking plagiarism",
  serpstat_seo_analyzer: "Analyzing SEO metrics",

  // PDF Generation
  sandbox_generate_pdf: "Generating PDF",

  // Odoo ERP Tools
  odoo_search_read: "Searching Odoo",
  odoo_create: "Creating in Odoo",
} as const;

// File operation text mappings
const FILE_OPERATION_TEXT_MAP = {
  create_file: "Creating file",
  read_file: "Reading file",
  delete_file: "Deleting file",
  rename_file: "Renaming file",
  move_file: "Moving file",
  copy_file: "Copying file",
  create_folder: "Creating folder",
} as const;

// File extension icon mappings
const FILE_EXTENSION_ICON_MAP = {
  md: FileText,
  markdown: FileText,
  txt: FileText,
  csv: FileSpreadsheet,
  json: FileJson,
  js: FileCode,
  ts: FileCode,
  html: FileType,
  py: FileCode,
  xml: FileText,
  pdf: FileText,
  docx: FileText,
  doc: FileText,
  xls: FileText,
  xlsx: FileText,
  pptx: FileText,
  ppt: FileText,
} as const;

export const getToolText = (toolName: string, query?: string): string => {
  const baseText = TOOL_TEXT_MAP[toolName as keyof typeof TOOL_TEXT_MAP];

  if (baseText) {
    // Special handling for search tools that need query appended
    if (
      (toolName === "search" ||
        toolName === "search_web" ||
        toolName === "website_scraping") &&
      query
    ) {
      return `${baseText}: ${query}`;
    }
    return baseText;
  }

  return "Processing request...";
};

export const getFileOperationText = (operation: string): string => {
  const operationText =
    FILE_OPERATION_TEXT_MAP[operation as keyof typeof FILE_OPERATION_TEXT_MAP];
  return operationText || "Working on the file";
};

export const getFileOperationIcon = (filePath = "") => {
  const extension = filePath.split(".").pop()?.toLowerCase();

  if (!extension) {
    return FileText;
  }

  const iconComponent =
    FILE_EXTENSION_ICON_MAP[extension as keyof typeof FILE_EXTENSION_ICON_MAP];
  return iconComponent || FileText;
};

/**
 * Badge color mappings for file extensions.
 */
const BADGE_COLORS: Record<string, string> = {
  PDF: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  DOC: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  DOCX: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  XLS: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  XLSX: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  PNG: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  JPG: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  JPEG: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  CSV: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  JSON: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  TXT: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

/**
 * Get the appropriate badge color class for a file extension.
 */
export const getBadgeColor = (extension: string): string => {
  return BADGE_COLORS[extension] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
};
